import React, {
	memo,
	useCallback,
	useMemo,
	useState,
	Dispatch,
	SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import MultiSelect from "../../../../../../../../../../components/common/LocalMultiSelect";
import { BankTransaction } from "../../../../../../../../../../services";
import { useTypedSelector } from "../../../../../../../../../../redux/store";
import { useObjectEditor } from "../../../../../../../../../../hooks";
import {
	generateAccessName,
	AccessKey,
	hasAccess,
} from "../../../../../../../../../../access";
import Modal from "../../../../../../../../../../components/Modal";
import {
	StyledColumn,
	StyledGrid,
	CheckBoxWithText,
} from "../../../../../../../../../../components/common";

import { Header, Footer } from "./components";
import { applyFilters } from "./utils";
import { DEFAUT_VALUEE_FILTERS } from "./constants";
import { ValueDefaultFilters } from "./constants/interface";

const StyledColumnWrap = styled(StyledColumn)`
	overflow: hidden;
	border-radius: 10px;
	background-color: white;
`;

const FiltersModal: React.FC<FiltersModal.Props> = ({
	value,
	onChange,
	isOpenFilterModal,
	setOpenFilterModal,
	accessSection,
}): JSX.Element => {
	const _value = useMemo(() => value, [value]);
	const { t } = useTranslation();

	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	const accessBtnGroup = useMemo(() => {
		const accessKeys = [
			{
				key: "remountTransactionType",
				accessKey: AccessKey.REMOUNT_TRANSACTION_TYPE,
			},
			{
				key: "status",
				accessKey: AccessKey.STATUS,
			},
			{
				key: "transactionType",
				accessKey: AccessKey.TRANSACTION_TYPE,
			},
		];

		if (!accessSection) {
			return Object.fromEntries(accessKeys.map(({ key }) => [key, true]));
		}

		return Object.fromEntries(
			accessKeys.map(({ key, accessKey }) => [
				key,
				hasAccess(
					generateAccessName(...accessSection, accessKey),
					personalRole,
				),
			]),
		);
	}, [accessSection, personalRole]);

	const [valueModalFilter, setValueModalFilter] =
		useState<FiltersModal.Value>(_value);

	const onCloseModal = useCallback(() => {
		setOpenFilterModal(false);
	}, [setOpenFilterModal]);

	const gridSettings = useMemo(
		() => ({
			areas: "",
			gap: "10px",
			alignItems: "center",
			columns: "1fr 2fr",
			w: "100%",
		}),
		[],
	);

	const valueEditor = useObjectEditor(valueModalFilter, setValueModalFilter);

	const remountTransactionType = valueEditor.useGetter(
		"remountTransactionType",
	);
	const setRemountTransactionType = valueEditor.useSetter(
		"remountTransactionType",
	);
	const checkRemountTransactionType = valueEditor.useGetter(
		"checkRemountTransactionType",
	);
	const setCheckRemountTransactionType = valueEditor.useSetter(
		"checkRemountTransactionType",
	);

	const status = valueEditor.useGetter("status");
	const setStatus = valueEditor.useSetter("status");
	const checkStatus = valueEditor.useGetter("checkStatus");
	const setCheckStatus = valueEditor.useSetter("checkStatus");

	const transactionType = valueEditor.useGetter("transactionType");
	const setTransactionType = valueEditor.useSetter("transactionType");
	const checkTransactionType = valueEditor.useGetter("checkTransactionType");
	const setCheckTransactionType = valueEditor.useSetter(
		"checkTransactionType",
	);

	const textTranslates: Record<string, string> = useMemo(() => {
		const getTranslation = (key: string) =>
			t(
				`pages.mainPage.pages.archives.tabs.bankTransactions.header.filter.${key}`,
			) ?? "";

		const getTranslationLabel = (key: string) =>
			t(
				`pages.mainPage.pages.archives.tabs.bankTransactions.content.columns.${key}`,
			) ?? "";
		const getTranslationChoise = (key: string) =>
			t(
				`pages.mainPage.pages.archives.tabs.bankTransactions.header.filter.choiseGroup.${key}`,
			) ?? "";

		return {
			title: getTranslation("title"),
			// btn
			reset: getTranslation("reset"),
			apply: getTranslation("apply"),
			cancel: getTranslation("cancel"),

			// label
			remountTransactionType: getTranslationLabel(
				"remountTransactionType",
			),
			remountTransactionTypeChoise: getTranslationChoise(
				"remountTransactionTypeChoise",
			),
			status: getTranslationLabel("status"),
			statusChoise: getTranslationChoise("statusChoise"),
			transactionType: getTranslationLabel("transactionType"),
			transactionTypeChoise: getTranslationChoise(
				"transactionTypeChoise",
			),
		};
	}, [t]);

	const handleClearBtn = useCallback(() => {
		setValueModalFilter(DEFAUT_VALUEE_FILTERS);
	}, []);

	const handleCancelBtn = useCallback(() => {
		setValueModalFilter(_value);
		onCloseModal();
	}, [onCloseModal, _value]);

	const handleApplyBtn = useCallback(() => {
		onChange(valueModalFilter);
		onCloseModal();
	}, [onChange, onCloseModal, valueModalFilter]);

	const remountTransactionTypeOptions = useMemo(
		() =>
			Object.values(BankTransaction.RemountTransactionType).map(
				(item, index) => ({
					key: index,
					label:
						t(
							`pages.mainPage.pages.archives.tabs.bankTransactions.content.remountTransactionType.${item}`,
						) ?? item,
					value: item,
				}),
			),
		[t],
	);

	const statusOptions = useMemo(
		() =>
			Object.values(BankTransaction.PaymentTransactionStatus).map(
				(item, index) => ({
					key: index,
					label:
						t(
							`pages.mainPage.pages.archives.tabs.bankTransactions.content.paymentTransactionStatus.${item}`,
						) ?? item,
					value: item,
				}),
			),
		[t],
	);

	const transactionTypeOptions = useMemo(() => {
		const pathT =
			"pages.mainPage.pages.archives.tabs.bankTransactions.content.bankTransactionType.";
		const typeTranslationMap: Record<string, string> = {
			order_payment: `${pathT}paymentForOrder`,
			replenishment_executor_card: `${pathT}replenishmentExecutorCard`,
			replenishment_customer_card: `${pathT}replenishmentCustomerCard`,
		};

		return Object.values(BankTransaction.BankTransactionType)
			.filter(
				(item) =>
					item !==
					BankTransaction.BankTransactionType
						.REPLENISHMENT_EXECUTOR_CARD,
			)
			.map((item, index) => {
				const translationKey =
					typeTranslationMap[item] ?? `${pathT}${item}`;

				return {
					key: index,
					label: t(translationKey) ?? item,
					value: item,
				};
			});
	}, [t]);

	const handleRemountTransactionType = useCallback(
		(selectedKeys: any[]) => {
			const values = remountTransactionTypeOptions
				.filter((item) => selectedKeys.includes(item.key))
				.map((item) => item.value);
			setRemountTransactionType(values);
		},
		[remountTransactionTypeOptions, setRemountTransactionType],
	);

	const handleStatus = useCallback(
		(selectedKeys: any[]) => {
			const values = statusOptions
				.filter((item) => selectedKeys.includes(item.key))
				.map((item) => item.value);
			setStatus(values);
		},
		[setStatus, statusOptions],
	);

	const handleTransactionType = useCallback(
		(selectedKeys: any[]) => {
			const values = transactionTypeOptions
				.filter((item) => selectedKeys.includes(item.key))
				.map((item) => item.value);
			setTransactionType(values);
		},
		[setTransactionType, transactionTypeOptions],
	);

	const getSelectedKeys = (
		values: string[],
		options: { key: number; value: string }[],
	) =>
		options
			.filter((item) => values.includes(item.value))
			.map((item) => item.key);

	return (
		<>
			{isOpenFilterModal && (
				<Modal dimmed>
					<StyledColumnWrap
						w="650px"
						h="76%"
						position="relative"
						p="6px 12px"
					>
						<Header
							title={textTranslates.title}
							onClose={handleCancelBtn}
						/>
						<StyledColumn
							w="100%"
							h="100%"
							p="1rem"
							justify="start"
							alignItems="center"
							gap="10px"
							overY="auto"
						>
							<StyledColumn gap="10px" w="100%">
								{accessBtnGroup.remountTransactionType && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={
												textTranslates.remountTransactionType
											}
											value={checkRemountTransactionType}
											onChange={
												setCheckRemountTransactionType
											}
										/>

										<MultiSelect
											value={getSelectedKeys(
												remountTransactionType,
												remountTransactionTypeOptions,
											)}
											all
											placeholder={
												textTranslates.remountTransactionTypeChoise
											}
											options={
												remountTransactionTypeOptions
											}
											onChange={
												handleRemountTransactionType
											}
											disabled={
												!checkRemountTransactionType
											}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.status && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.status}
											value={checkStatus}
											onChange={setCheckStatus}
										/>

										<MultiSelect
											value={getSelectedKeys(
												status,
												statusOptions,
											)}
											all
											placeholder={
												textTranslates.statusChoise
											}
											options={statusOptions}
											onChange={handleStatus}
											disabled={!checkStatus}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.transactionType && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={
												textTranslates.transactionType
											}
											value={checkTransactionType}
											onChange={setCheckTransactionType}
										/>

										<MultiSelect
											value={getSelectedKeys(
												transactionType,
												transactionTypeOptions,
											)}
											all
											placeholder={
												textTranslates.transactionTypeChoise
											}
											options={transactionTypeOptions}
											onChange={handleTransactionType}
											disabled={!checkTransactionType}
										/>
									</StyledGrid>
								)}
							</StyledColumn>
						</StyledColumn>

						<Footer
							texts={{
								reset: textTranslates.reset,
								cancel: textTranslates.cancel,
								apply: textTranslates.apply,
							}}
							onReset={handleClearBtn}
							onCancel={handleCancelBtn}
							onApply={handleApplyBtn}
						/>
					</StyledColumnWrap>
				</Modal>
			)}
		</>
	);
};

declare namespace FiltersModal {
	interface Props {
		value: Value;
		onChange: (value: Value) => void;
		isOpenFilterModal: boolean;
		setOpenFilterModal: Dispatch<SetStateAction<boolean>>;
		accessSection?: AccessKey[];
	}

	interface Value extends ValueDefaultFilters {}
}

export { applyFilters, DEFAUT_VALUEE_FILTERS };

export const FiltersModalMemo = memo(FiltersModal);
export default FiltersModal;
